'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, User, ChevronDown, Globe, Sparkles, LogOut, Shield, GraduationCap, Building2 } from 'lucide-react';
import { useAuth } from '@/lib/authContext';
import NotificationBell from '@/components/NotificationBell';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'hi', label: 'हिंदी', flag: '🇮🇳' },
  { code: 'ta', label: 'தமிழ்', flag: '🇮🇳' },
  { code: 'te', label: 'తెలుగు', flag: '🇮🇳' },
  { code: 'bn', label: 'বাংলা', flag: '🇮🇳' },
  { code: 'mr', label: 'मराठी', flag: '🇮🇳' },
  { code: 'gu', label: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'kn', label: 'ಕನ್ನಡ', flag: '🇮🇳' },
];

const navLinks = [
  { label: 'Home', href: '/home' },
  { label: 'Colleges', href: '/' },
  { label: 'Exams', href: '/exam-cutoff-info' },
  { label: 'Predictor', href: '/college-predictor' },
  {
    label: 'Explore',
    href: '#',
    children: [
      { label: 'City-wise Search', href: '/city-search' },
      { label: 'Compare Colleges', href: '/compare-colleges' },
      { label: 'Course Finder', href: '/course-finder' },
      { label: 'Student Counselling', href: '/counselling' },
      { label: 'Study Abroad', href: '/study-abroad' },
      { label: 'College to Career', href: '/college-to-career' },
      { label: '1 App → 1000+ Colleges', href: '/one-application' },
      { label: 'Application Tracker', href: '/application-tracker' },
      { label: 'Apply to College', href: '/apply' },
    ],
  },
  { label: 'Write a Review', href: '/write-review' },
  { label: '🏆 Leaderboard', href: '/influencer-leaderboard' },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(languages?.[0]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = () => { signOut(); setUserMenuOpen(false); router?.push('/home'); };

  const adminHref = profile?.role === 'super_admin' ? '/admin/super' : profile?.role === 'college_manager' ? '/admin/college-manager' : '/admin/login';

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown="lang"]')) setLangOpen(false);
      if (!target.closest('[data-dropdown="explore"]')) setExploreOpen(false);
      if (!target.closest('[data-dropdown="user"]')) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      {/* Top announcement bar */}
      <div className="bg-primary text-white text-xs py-1.5 px-4 text-center font-medium">
        ✨ AI-Powered College Matching is Live — Find Your Perfect College in Seconds
        <Link href="/one-application" className="ml-2 underline font-semibold hover:opacity-80 transition-opacity">
          Apply to 1000+ Colleges →
        </Link>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link href="/home" className="flex items-center gap-2 shrink-0">
            <AppLogo size={90} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {navLinks?.map((link) => {
              if (link?.children) {
                const isActive = link?.children?.some((c) => pathname?.startsWith(c?.href));
                return (
                  <div key={`nav-${link?.label}`} className="relative" data-dropdown="explore">
                    <button
                      onClick={() => setExploreOpen((prev) => !prev)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-all duration-150 whitespace-nowrap ${
                        isActive ? 'bg-accent text-primary font-700' : 'text-foreground hover:bg-muted hover:text-primary'
                      }`}
                      style={{ fontWeight: isActive ? 700 : 600 }}
                    >
                      {link?.label}
                      <ChevronDown size={14} className={`transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {exploreOpen && (
                      <div
                        className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50 animate-fade-in"
                      >
                        {link?.children?.map((child) => (
                          <Link
                            key={child?.href}
                            href={child?.href}
                            onClick={() => setExploreOpen(false)}
                            className={`flex items-center px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-muted hover:text-primary ${pathname?.startsWith(child?.href) ? 'text-primary bg-accent' : 'text-foreground'}`}
                          >
                            {child?.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const isActive =
                link?.href === '/home'
                  ? pathname === '/home'
                  : link?.href === '/'
                  ? pathname === '/'
                  : pathname?.startsWith(link?.href);
              return (
                <Link
                  key={`nav-${link?.label}`}
                  href={link?.href}
                  className={`px-3 py-2 rounded-lg text-sm transition-all duration-150 whitespace-nowrap ${
                    isActive
                      ? 'bg-accent text-primary' : 'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                  style={{ fontWeight: isActive ? 700 : 600 }}
                >
                  {link?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <div className="relative hidden sm:block" data-dropdown="lang">
              <button
                onClick={() => setLangOpen((prev) => !prev)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                aria-label="Select language"
                aria-expanded={langOpen}
              >
                <Globe size={14} className="text-primary" />
                <span className="hidden xl:inline">{selectedLang?.label}</span>
                <span className="xl:hidden">{selectedLang?.flag}</span>
                <ChevronDown size={12} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div
                  className="absolute top-full right-0 mt-1 w-44 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50 animate-fade-in"
                >
                  {languages?.map((lang) => (
                    <button
                      key={lang?.code}
                      onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-muted hover:text-primary text-left ${selectedLang?.code === lang?.code ? 'text-primary bg-accent' : 'text-foreground'}`}
                    >
                      <span>{lang?.flag}</span>
                      <span>{lang?.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth section */}
            {user ? (
              <div className="relative hidden sm:block" data-dropdown="user">
                <button
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                    {profile?.full_name?.charAt(0) ?? user?.email?.charAt(0)?.toUpperCase() ?? 'U'}
                  </div>
                  <span className="hidden lg:inline max-w-[80px] truncate">{profile?.full_name?.split(' ')?.[0] ?? user?.email?.split('@')?.[0]}</span>
                  <ChevronDown size={12} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute top-full right-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50 animate-fade-in"
                  >
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-bold text-foreground">{profile?.full_name ?? user?.email}</p>
                      <p className="text-xs text-muted-foreground capitalize">{profile?.role?.replace('_', ' ') ?? 'user'}</p>
                    </div>
                    {profile?.role === 'student' && (
                      <Link href="/student-profile" onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted hover:text-primary transition-colors">
                        <GraduationCap size={14} />My Profile
                      </Link>
                    )}
                    {(profile?.role === 'super_admin' || profile?.role === 'college_manager') && (
                      <Link href={adminHref} onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted hover:text-primary transition-colors">
                        {profile?.role === 'super_admin' ? <Shield size={14} /> : <Building2 size={14} />}
                        Admin Dashboard
                      </Link>
                    )}
                    <button onClick={handleSignOut}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors">
                      <LogOut size={14} />Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link href="/sign-in"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  <User size={15} />Sign In
                </Link>
              </div>
            )}

            {/* Notification Bell - only for logged in users */}
            {user && <NotificationBell />}

            <Link href="/one-application" className="hidden sm:flex items-center gap-1.5 btn-primary text-sm py-1.5 px-4">
              <Sparkles size={14} />
              Apply Now
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border py-3 space-y-1 animate-fade-in">
            {navLinks?.map((link) => {
              if (link?.children) {
                return (
                  <div key={`mobile-nav-${link?.label}`}>
                    <p className="px-3 py-1.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">{link?.label}</p>
                    {link?.children?.map((child) => (
                      <Link
                        key={child?.href}
                        href={child?.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${pathname?.startsWith(child?.href) ? 'bg-accent text-primary' : 'text-foreground hover:bg-muted'}`}
                      >
                        {child?.label}
                      </Link>
                    ))}
                  </div>
                );
              }
              const isActive =
                link?.href === '/home'
                  ? pathname === '/home'
                  : link?.href === '/'
                  ? pathname === '/'
                  : pathname?.startsWith(link?.href);
              return (
                <Link
                  key={`mobile-nav-${link?.label}`}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    isActive ? 'bg-accent text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
            {/* Mobile Language Selector */}
            <div className="px-3 pt-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Language</p>
              <div className="flex flex-wrap gap-2">
                {languages?.map((lang) => (
                  <button
                    key={lang?.code}
                    onClick={() => setSelectedLang(lang)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${selectedLang?.code === lang?.code ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:bg-muted'}`}
                  >
                    {lang?.flag} {lang?.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-2 flex flex-col gap-2 px-1">
              {user ? (
                <>
                  {profile?.role === 'student' && (
                    <Link href="/student-profile" onClick={() => setMobileOpen(false)} className="btn-secondary justify-center text-sm py-2">
                      My Profile
                    </Link>
                  )}
                  {(profile?.role === 'super_admin' || profile?.role === 'college_manager') && (
                    <Link href={adminHref} onClick={() => setMobileOpen(false)} className="btn-secondary justify-center text-sm py-2">
                      Admin Dashboard
                    </Link>
                  )}
                  <button onClick={() => { handleSignOut(); setMobileOpen(false); }} className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors">
                    <LogOut size={14} />Sign Out
                  </button>
                </>
              ) : (
                <Link href="/sign-in" onClick={() => setMobileOpen(false)} className="btn-secondary justify-center text-sm py-2">
                  Sign In
                </Link>
              )}
              <Link href="/one-application" onClick={() => setMobileOpen(false)} className="btn-primary justify-center">
                Apply to 1000+ Colleges
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}